/// <amd-module name="Build/Extensions/Connection-Services/ServiceConnectionCreationService" />
import { openLinkSecurely } from "Build/Common/Library/Navigation";
import { ServiceConnectionInfo } from "Build/Extensions/Connection-Services/ServiceConnectionRetrieverService";
import * as Resources from "Build/Extensions/Connection-Services/Resources";
import { RepositoryTypes } from "Build/SourceProviders/Common";

import { getConnectedServiceClient } from "DistributedTask/ConnectedService/Client/RestClient/ConnectedService";
import { EndpointAuthorization, ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { getServiceEndpointClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";

import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { Uri } from "VSS/Platform/Util/Url";

import { format } from "VSS/Core/Util/String";

export namespace Constant {
    export const AvatarUrl = "AvatarUrl";
    export const OAuthScheme = "OAuth";
    export const OAuth2Scheme = "OAuth2";
    export const PersonalAccessTokenScheme = "PersonalAccessToken";
    export const TokenScheme = "Token";
    export const UserPassScheme = "UsernamePassword";
}

export interface UserAuthorization {
    scheme: string;
    displayName: string;
    avatarUrl: string;
    strongboxKey: string; // oauth
    personalAccessToken: string; // pat
}

export interface IServiceConnectionCreationService extends IVssService {
    createEndpointName(
        serviceConnections: ServiceConnectionInfo[],
        originalName: string): string;

    createAuthRequest(
        context: IVssComponentContext,
        sourceProvider: string,
        projectId: string,
        useWellKnownStrongBoxLocation: boolean,
        handleError: (e: any) => void): void;

    createOrUpdateEndpointForUserAuth(
        context: IVssComponentContext,
        sourceProvider: string,
        projectId: string,
        serviceConnections: ServiceConnectionInfo[],
        userAuthorization: UserAuthorization,
        findPredicate: (value: ServiceConnectionInfo) => boolean): Promise<ServiceEndpoint>;
}

interface ServiceEndpointConstants {
    scope: string;
    url: string;
    userDisplayName: string;
    configurationId: string;
    sourceProviderDisplayName: string;
}

const SourceProviderConstants: { [key: string]: ServiceEndpointConstants } = {
    [RepositoryTypes.Bitbucket]: { scope: "", url: "https://api.bitbucket.org", userDisplayName: "DisplayName", configurationId: "744a24d8-54a8-4947-9320-d63745191082", sourceProviderDisplayName: "Bitbucket" },
    [RepositoryTypes.GitHub]: { scope: "repo,read:user,user:email,admin:repo_hook", url: "http://github.com", userDisplayName: "GitHubHandle", configurationId: "00000000-0000-0000-0000-000000000000", sourceProviderDisplayName: "GitHub" }
}

// This class is exported for testing
export class ServiceConnectionCreationService extends VssService implements IServiceConnectionCreationService {
    public createEndpointName(serviceConnections: ServiceConnectionInfo[], originalName: string): string {
        let count = 1;
        let endpointName = originalName;
        const namesSet = new Set(serviceConnections.map(item => item.name));

        while (namesSet.has(endpointName)) {
            endpointName = format(Resources.ConnectionNameFormat, originalName, count);
            count++;
        }

        return endpointName;
    }

    public async createAuthRequest(context: IVssComponentContext, sourceProvider: string, projectId: string, useWellKnownStrongBoxLocation: boolean, handleError: (e: any) => void) {
        const connectedServiceClient = getConnectedServiceClient(context.pageContext);

        let queryParams = `redirectUrl=${encodeURIComponent(new Uri(window.location.href, { absoluteUriRequired: true }).absoluteUri)}`;
        if (useWellKnownStrongBoxLocation) {
            queryParams = queryParams.concat("&useWellKnownStrongBoxLocation=true");
        }

        try {
            const sourceProviderDetails = SourceProviderConstants[sourceProvider.toLowerCase()];
            const scope = sourceProviderDetails.scope;
            const configurationId = sourceProviderDetails.configurationId;
            const authRequest = await connectedServiceClient.createAuthRequest(undefined!, projectId, sourceProvider, configurationId, scope, queryParams);
            if (authRequest.errorMessage) {
                throw new Error(authRequest.errorMessage);
            }
            openLinkSecurely(authRequest.url);
        } catch (error) {
            const errorMessage = error && error.message || format(Resources.AuthorizationFailure, sourceProvider);
            handleError(errorMessage);
        }
    }

    public createOrUpdateEndpointForUserAuth(
        context: IVssComponentContext,
        sourceProvider: string,
        projectId: string,
        serviceConnections: ServiceConnectionInfo[],
        userAuthorization: UserAuthorization,
        findPredicate: (value: ServiceConnectionInfo) => boolean): Promise<ServiceEndpoint> {

        serviceConnections = serviceConnections || [];

        const authorizationInfo: EndpointAuthorization = {
            scheme: userAuthorization.scheme,
        } as EndpointAuthorization;

        const sourceProviderDetails = SourceProviderConstants[sourceProvider.toLowerCase()];

        if (userAuthorization.scheme === Constant.OAuthScheme) {
            authorizationInfo.parameters = {
                accessToken: userAuthorization.strongboxKey
            };
        } else if (userAuthorization.scheme === Constant.OAuth2Scheme) {
            authorizationInfo.parameters = {
                accessToken: userAuthorization.strongboxKey,
                configurationId: sourceProviderDetails.configurationId,
            };
        } else if (userAuthorization.scheme === Constant.PersonalAccessTokenScheme) {
            authorizationInfo.parameters = {
                password: userAuthorization.personalAccessToken
            };
        }

        const data: { [key: string]: string } = {
            AvatarUrl: userAuthorization.avatarUrl,
            [sourceProviderDetails.userDisplayName]: userAuthorization.displayName
        };

        const serviceConnection: ServiceEndpoint = {
            type: sourceProvider,
            url: sourceProviderDetails.url,
            authorization: authorizationInfo,
            data: data
        } as ServiceEndpoint;

        const connectionOfUser: ServiceConnectionInfo | undefined = serviceConnections.find(findPredicate);
        const serviceEndpointClient = getServiceEndpointClient(context.pageContext);
        if (!!connectionOfUser) {
            serviceConnection.id = connectionOfUser.id;
            serviceConnection.name = connectionOfUser.name;
            this._fillServiceEndpointProjectReferences(serviceConnection, projectId);
            return serviceEndpointClient.updateServiceEndpoint(serviceConnection, serviceConnection.id);
        } else {
            serviceConnection.name = this.createEndpointName(serviceConnections, format(Resources.ConnectionDisplayName, sourceProviderDetails.sourceProviderDisplayName, userAuthorization.displayName));
            this._fillServiceEndpointProjectReferences(serviceConnection, projectId);
            return serviceEndpointClient.createServiceEndpoint(serviceConnection);
        }
    }

    private _fillServiceEndpointProjectReferences(endpoint: ServiceEndpoint, projectId: string): void {
        endpoint.serviceEndpointProjectReferences = [
            {
                name: endpoint.name,
                description: endpoint.description,
                projectReference: {
                    id: projectId,
                    name: ""
                }
            }
        ];
    }
}

Services.add("IServiceConnectionCreationService", { serviceFactory: ServiceConnectionCreationService });
